import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { 
    Bot, 
    Phone, 
    Users, 
    Plus, 
    Trash2, 
    AlertCircle,
    Info,
    Tag,
    ClipboardCheck,
    Target,
    HelpCircle,
    Check,
    Type,
    Link
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import type { RubricRequest, RubricType, CustomFieldPayloadItem } from '@/api/rubrics/types';
import type { Option } from '@/components/ui/badge-multi-select';
import { getAssistants } from "@/api/assistants/assistants.api";
import { getMultiAgents } from '@/api/multi-agent/multi_agent.api';
import { getPhoneNumbers } from '@/api/phone-numbers/phone_numbers.api';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';

// Define the possible data types
type CustomFieldDataType = 'string' | 'number' | 'boolean';

// Update KeyValuePair to include dataType and allow varying value types
interface CustomFieldItem {
    key: string;
    value: string | number | boolean | undefined; // Allow different types + undefined for initial state
    dataType: CustomFieldDataType;
}

interface ExtendedCustomField {
    items: CustomFieldItem[];
}

interface TransformedRubricData {
    name: string;
    type: RubricType;
    assistantIds: string[] | null;
    phoneNumberIds: string[] | null;
    multiAgentIds: string[] | null;
    successEvaluation: string | null;
    [key: string]: string | RubricType | string[] | null | CustomFieldItem[]; // Updated to use CustomFieldItem
}

interface RubricFormProps {
    initialData?: RubricRequest;
    onSubmit: (data: RubricRequest) => Promise<void>;
    formId: string;
}

const RESERVED_FIELD_NAMES = ['successEvaluation', 'name', 'type', 'assistantIds', 'phoneNumberIds', 'multiAgentIds'];

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.3 }
    }
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const RubricForm = ({ initialData, onSubmit, formId }: RubricFormProps) => {
    const { error: showError, info } = useEnhancedToast();
    const [formData, setFormData] = useState<RubricRequest>(initialData || {
        name: '',
        type: 'inboundPhoneCall',
        assistantIds: [],
        phoneNumberIds: [],
        multiAgentIds: [],
        successEvaluation: ''
    });

    // Update customFields state to use ExtendedCustomField and CustomFieldItem
    const [customFields, setCustomFields] = useState<Record<string, ExtendedCustomField>>(() => {
        if (!initialData) return {};
        
        const customFieldEntries = Object.entries(initialData).filter(([key, value]) => 
            !RESERVED_FIELD_NAMES.includes(key) && Array.isArray(value)
        );

        return customFieldEntries.reduce((acc, [key, value]) => {
            // Now assume incoming value is CustomFieldPayloadItem[] from the API
            const items = (value as CustomFieldPayloadItem[]).map(item => ({
                key: item.key,
                // The value from the API should already be the correct type (string, number, boolean)
                // If it's unexpectedly missing or null/undefined, default based on dataType
                value: item.value !== undefined && item.value !== null 
                       ? item.value 
                       : (item.dataType === 'boolean' ? false : (item.dataType === 'number' ? 0 : '')),
                dataType: item.dataType || 'string' // Fallback to string if somehow missing
            }));
            return {
                ...acc,
                [key]: { items } // Use 'items' 
            };
        }, {});
    });

    const [newFieldName, setNewFieldName] = useState('');
    
    // Options state for selects
    const [assistantOptions, setAssistantOptions] = useState<Option[]>([]);
    const [phoneNumberOptions, setPhoneNumberOptions] = useState<Option[]>([]);
    const [multiAgentOptions, setMultiAgentOptions] = useState<Option[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(true);

    useEffect(() => {
        const fetchOptions = async () => {
            setIsLoadingOptions(true);
            try {
                const [assistantsRes, phoneNumbersRes, multiAgentsRes] = await Promise.all([
                    getAssistants(),
                    getPhoneNumbers(),
                    getMultiAgents()
                ]);

                if (assistantsRes.data) {
                    setAssistantOptions(
                        assistantsRes.data.map(assistant => ({
                            label: assistant.name,
                            value: assistant.id
                        }))
                    );
                }

                if (phoneNumbersRes.data) {
                    setPhoneNumberOptions(
                        phoneNumbersRes.data.map(phone => ({
                            label: phone.number,
                            value: phone.id
                        }))
                    );
                }

                if (multiAgentsRes.data) {
                    setMultiAgentOptions(
                        multiAgentsRes.data.map(agent => ({
                            label: agent.name,
                            value: agent.id
                        }))
                    );
                }
            } catch (error) {
                console.error('Error fetching options:', error);
                showError({
                    title: 'Failed to Load Options',
                    description: 'Failed to load some options. Please try refreshing.',
                    icon: AlertCircle
                });
            } finally {
                setIsLoadingOptions(false);
            }
        };

        fetchOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            showError({
                title: 'Name Required',
                description: 'The name field is required to create a rubric.',
                icon: AlertCircle
            });
            return;
        }

        // Transform the form data
        const transformedData: TransformedRubricData = {
            name: formData.name,
            type: formData.type,
            // Convert empty arrays to null
            assistantIds: formData.assistantIds?.length ? formData.assistantIds : null,
            phoneNumberIds: formData.phoneNumberIds?.length ? formData.phoneNumberIds : null,
            multiAgentIds: formData.multiAgentIds?.length ? formData.multiAgentIds : null,
            // Convert empty string to null
            successEvaluation: formData.successEvaluation?.trim() || null
        };
        
        // Add current custom fields, parsing values based on dataType
        Object.entries(customFields).forEach(([fieldName, field]) => {
            transformedData[fieldName] = field.items.map(item => {
                let parsedValue: string | number | boolean | undefined = item.value;
                if (item.dataType === 'number') {
                    const num = Number(item.value);
                    parsedValue = isNaN(num) ? undefined : num; // Handle potential NaN
                } else if (item.dataType === 'boolean') {
                    if (typeof item.value === 'string') {
                       parsedValue = item.value.toLowerCase() === 'true';
                    } else {
                       parsedValue = Boolean(item.value); // Handle potential direct boolean value if UI sets it
                    }
                }
                // String remains as is
                return { 
                    key: item.key, 
                    value: parsedValue, 
                    dataType: item.dataType 
                };
            }).filter(item => item.value !== undefined); // Filter out items with undefined values (e.g., failed number parse)
        });

        // If we have initialData, check for removed custom fields
        if (initialData) {
            const initialCustomFields = Object.keys(initialData).filter(key => 
                !RESERVED_FIELD_NAMES.includes(key) && Array.isArray(initialData[key])
            );

            // Set removed fields to null
            initialCustomFields.forEach(fieldName => {
                if (!customFields[fieldName]) {
                    transformedData[fieldName] = null;
                }
            });
        }

        // Cast the transformed data to RubricRequest type
        onSubmit(transformedData as RubricRequest);
    };

    const addCustomField = () => {
        const fieldName = newFieldName.trim();
        if (!fieldName) return;

        if (RESERVED_FIELD_NAMES.includes(fieldName)) {
            showError({
                title: 'Invalid Field Name',
                description: 'This field name is reserved and cannot be used.',
                icon: AlertCircle
            });
            return;
        }
        
        if (customFields[fieldName]) {
            showError({
                title: 'Duplicate Field Name',
                description: 'A field with this name already exists.',
                icon: AlertCircle
            });
            return;
        }

        setCustomFields(prev => ({
            [fieldName]: {
                items: [{
                    key: '',
                    value: undefined, // Start with undefined value
                    dataType: 'string' // Default to string
                }]
            },
            ...prev
        }));
        setNewFieldName('');
    };

    // Update updateCustomField to handle dataType and different value types
    const updateCustomField = (
        fieldName: string, 
        itemIndex: number, 
        keyOrValueOrType: 'key' | 'value' | 'dataType', 
        value: string | number | boolean | undefined // Allow undefined here
    ) => {
        setCustomFields(prev => {
            const newItems = prev[fieldName].items.map((item, idx) => {
                if (idx === itemIndex) {
                    const updatedItem = { ...item, [keyOrValueOrType]: value };
                    // Reset value if dataType changes
                    if (keyOrValueOrType === 'dataType') {
                        updatedItem.value = updatedItem.dataType === 'boolean' ? false : undefined; // Default boolean to false, others to undefined
                    }
                    return updatedItem;
                }
                return item;
            });
            return {
                ...prev,
                [fieldName]: { items: newItems }
            };
        });
    };

    // Update addKeyValuePair to initialize with default CustomFieldItem
    const addKeyValuePair = (fieldName: string) => {
        setCustomFields(prev => ({
            ...prev,
            [fieldName]: {
                items: [...prev[fieldName].items, { key: '', value: undefined, dataType: 'string' }] // Use 'items'
            }
        }));
    };

    // Update removeKeyValuePair to use 'items'
    const removeKeyValuePair = (fieldName: string, itemIndex: number) => {
        if (customFields[fieldName].items.length === 1) {
            showError({
                title: 'Cannot Remove',
                description: 'At least one key-value pair is required.',
                icon: AlertCircle
            });
            return;
        }

        setCustomFields(prev => ({
            ...prev,
            [fieldName]: {
                items: prev[fieldName].items.filter((_, idx) => idx !== itemIndex) // Use 'items'
            }
        }));
    };

    const removeCustomField = (fieldName: string) => {
        setCustomFields(prev => {
            const newFields = { ...prev };
            delete newFields[fieldName];
            return newFields;
        });
        info({
            title: 'Field Removed',
            description: `Custom field "${fieldName}" has been removed.`,
            icon: Trash2
        });
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            <form id={formId} onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Information */}
                    <motion.div variants={itemVariants}>
                        <Card className="h-full">
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                        <ClipboardCheck className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <CardTitle>Basic Information</CardTitle>
                                        <CardDescription>Configure the basic settings for your rubric</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="name" className="flex items-center gap-2">
                                        <Type className="h-4 w-4 text-primary" />
                                        Name
                                        <span className="text-destructive">*</span>
                                    </Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter rubric name"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="type" className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-primary" />
                                        Call Type
                                    </Label>
                                    <Combobox
                                        options={[
                                            { value: 'inboundPhoneCall', label: 'Inbound Call' },
                                            { value: 'outboundPhoneCall', label: 'Outbound Call' }
                                        ]}
                                        value={formData.type}
                                        onValueChange={(value) => setFormData(prev => ({ ...prev, type: value as RubricType }))}
                                        placeholder="Select call type..."
                                        searchPlaceholder="Search call types..."
                                        emptyMessage="No call type found."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="successEvaluation" className="flex items-center gap-2">
                                        <Target className="h-4 w-4 text-primary" />
                                        Success Evaluation
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-4 w-4 p-0 hover:bg-transparent" type="button">
                                                        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                                                        <span className="sr-only">About success evaluation</span>
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent className="w-[320px] p-0" align="start">
                                                    <div className="px-4 py-3 border-b bg-muted/30">
                                                        <p className="font-semibold text-sm">Recommended Evaluation Scales</p>
                                                        <p className="text-xs text-muted-foreground mt-0.5">Configure these in your Assistant's Analysis settings</p>
                                                    </div>
                                                    <div className="p-3 space-y-2">
                                                        <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                                <Check className="h-3 w-3 text-primary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium">Numeric Scale</p>
                                                                <p className="text-xs text-muted-foreground">e.g., 1-10</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                                <Check className="h-3 w-3 text-primary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium">Descriptive Scale</p>
                                                                <p className="text-xs text-muted-foreground">e.g., Poor, Fair, Good, Excellent</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                                <Check className="h-3 w-3 text-primary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium">Percentage Scale</p>
                                                                <p className="text-xs text-muted-foreground">e.g., 0-100%</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                                <Check className="h-3 w-3 text-primary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium">Likert Scale</p>
                                                                <p className="text-xs text-muted-foreground">e.g., Strongly Disagree to Agree</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                                <Check className="h-3 w-3 text-primary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-medium">Pass/Fail</p>
                                                                <p className="text-xs text-muted-foreground">Binary evaluation</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </Label>
                                    <Textarea
                                        id="successEvaluation"
                                        value={formData.successEvaluation}
                                        onChange={(e) => setFormData(prev => ({ ...prev, successEvaluation: e.target.value }))}
                                        placeholder="Define your success criteria based on the selected scale (e.g., 'Rating must be Excellent on Poor/Fair/Good/Excellent scale')"
                                        className="min-h-[100px]"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Connections */}
                    <motion.div variants={itemVariants}>
                        <Card className="h-full">
                            <CardHeader>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                            <Users className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex-1 space-y-1">
                                            <CardTitle>Connections</CardTitle>
                                            <CardDescription>Configure associated resources</CardDescription>
                                        </div>
                                    </div>
                                    <TooltipProvider delayDuration={200}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" type="button">
                                                    <Info className="h-4 w-4 text-muted-foreground" />
                                                    <span className="sr-only">About connections</span>
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="left" align="start" className="w-[320px] p-0">
                                                <div className="px-4 py-3 border-b bg-muted/30">
                                                    <p className="font-semibold text-sm">Flexible Resource Linking</p>
                                                    <p className="text-xs text-muted-foreground mt-0.5">Link criteria to any combination of resources</p>
                                                </div>
                                                <div className="p-3 space-y-2">
                                                    <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                            <Link className="h-3 w-3 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-medium">Assistants</p>
                                                            <p className="text-xs text-muted-foreground">Link to one or more AI assistants</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                            <Link className="h-3 w-3 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-medium">Phone Numbers</p>
                                                            <p className="text-xs text-muted-foreground">Associate with specific phone lines</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors">
                                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 mt-0.5">
                                                            <Link className="h-3 w-3 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-medium">Multi-Agent Systems</p>
                                                            <p className="text-xs text-muted-foreground">Link to collaborative AI setups</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="px-4 py-2.5 border-t bg-muted/20">
                                                    <p className="text-xs text-muted-foreground">
                                                        💡 Mix and match to create comprehensive evaluation setups
                                                    </p>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Bot className="h-4 w-4 text-primary" />
                                        Assistants
                                    </Label>
                                    {isLoadingOptions ? (
                                        <div className="relative w-full">
                                            <div className="w-full flex items-start justify-between h-auto min-h-[2.5rem] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                                                <div className="flex flex-wrap gap-1 pe-2">
                                                    {[1, 2, 3].map((i) => (
                                                        <div key={i} className="inline-flex items-center rounded-sm px-1 font-mono bg-secondary text-secondary-foreground">
                                                            <div className="flex items-center gap-1">
                                                                <Skeleton className="h-4 w-24" />
                                                                <div className="flex items-center justify-center h-4 w-4">
                                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="h-4 w-4 shrink-0 opacity-50 ms-2">
                                                    <Skeleton className="h-4 w-4" />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Combobox
                                            mode="multiple"
                                            options={assistantOptions.map(opt => ({
                                                value: opt.value,
                                                label: opt.label,
                                                description: opt.value  // Show ID as description
                                            }))}
                                            value={formData.assistantIds || []}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, assistantIds: value }))}
                                            placeholder="Select assistants..."
                                            searchPlaceholder="Search assistants..."
                                            emptyMessage="No assistants available."
                                            maxDisplay={3}
                                        />
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-primary" />
                                        Phone Numbers
                                    </Label>
                                    {isLoadingOptions ? (
                                        <div className="relative w-full">
                                            <div className="w-full flex items-start justify-between h-auto min-h-[2.5rem] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                                                <div className="flex flex-wrap gap-1 pe-2">
                                                    {[1, 2].map((i) => (
                                                        <div key={i} className="inline-flex items-center rounded-sm px-1 font-mono bg-secondary text-secondary-foreground">
                                                            <div className="flex items-center gap-1">
                                                                <Skeleton className="h-4 w-32" />
                                                                <div className="flex items-center justify-center h-4 w-4">
                                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="h-4 w-4 shrink-0 opacity-50 ms-2">
                                                    <Skeleton className="h-4 w-4" />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Combobox
                                            mode="multiple"
                                            options={phoneNumberOptions.map(opt => ({
                                                value: opt.value,
                                                label: opt.label,
                                                description: opt.value  // Show ID as description
                                            }))}
                                            value={formData.phoneNumberIds || []}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, phoneNumberIds: value }))}
                                            placeholder="Select phone numbers..."
                                            searchPlaceholder="Search phone numbers..."
                                            emptyMessage="No phone numbers available."
                                            maxDisplay={3}
                                        />
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Users className="h-4 w-4 text-primary" />
                                        Multi-Agents
                                    </Label>
                                    {isLoadingOptions ? (
                                        <div className="relative w-full">
                                            <div className="w-full flex items-start justify-between h-auto min-h-[2.5rem] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                                                <div className="flex flex-wrap gap-1 pe-2">
                                                    {[1, 2].map((i) => (
                                                        <div key={i} className="inline-flex items-center rounded-sm px-1 font-mono bg-secondary text-secondary-foreground">
                                                            <div className="flex items-center gap-1">
                                                                <Skeleton className="h-4 w-28" />
                                                                <div className="flex items-center justify-center h-4 w-4">
                                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="h-4 w-4 shrink-0 opacity-50 ms-2">
                                                    <Skeleton className="h-4 w-4" />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Combobox
                                            mode="multiple"
                                            options={multiAgentOptions.map(opt => ({
                                                value: opt.value,
                                                label: opt.label,
                                                description: opt.value  // Show ID as description
                                            }))}
                                            value={formData.multiAgentIds || []}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, multiAgentIds: value }))}
                                            placeholder="Select multi-agents..."
                                            searchPlaceholder="Search multi-agents..."
                                            emptyMessage="No multi-agents available."
                                            maxDisplay={3}
                                        />
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>

                {/* Custom Evaluation Criteria */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div variants={itemVariants}>
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                        <ClipboardCheck className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <CardTitle>Custom Evaluation Criteria</CardTitle>
                                        <CardDescription>Add custom evaluation criteria for your rubric</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-4">
                                    <div className="flex gap-2">
                                        <div className="flex-1 space-y-2">
                                            <Label>Field Name</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    value={newFieldName}
                                                    onChange={(e) => setNewFieldName(e.target.value)}
                                                    placeholder="Enter field name"
                                                    className="flex-1"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            addCustomField();
                                                        }
                                                    }}
                                                />
                                                <TooltipProvider delayDuration={200}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                type="button"
                                                                onClick={addCustomField}
                                                                disabled={!newFieldName.trim()}
                                                            >
                                                                <Plus className="h-4 w-4 mr-2" />
                                                                Add Field
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p>Add a new custom evaluation field</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {Object.entries(customFields).length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            className="space-y-4"
                                        >
                                            {Object.entries(customFields).map(([fieldName, field]) => (
                                                <motion.div
                                                    key={fieldName}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -20 }}
                                                >
                                                    <Card className="overflow-hidden">
                                                        <CardHeader>
                                                            <div className="flex items-center gap-4">
                                                                <div className="flex items-center gap-3 flex-1">
                                                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                                                        <Tag className="h-4 w-4 text-primary" />
                                                                    </div>
                                                                    <div className="flex-1 space-y-1">
                                                                        <CardTitle>{fieldName}</CardTitle>
                                                                        <CardDescription>Custom evaluation field</CardDescription>
                                                                    </div>
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => removeCustomField(fieldName)}
                                                                    className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                    <span className="sr-only">Remove field</span>
                                                                </Button>
                                                            </div>
                                                        </CardHeader>
                                                        <CardContent className="space-y-4">
                                                        {field.items.map((item, itemIndex) => (
                                                            <motion.div
                                                                key={itemIndex}
                                                                initial={{ opacity: 0, y: 10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -10 }}
                                                                className="relative"
                                                            >
                                                                <div className="grid gap-4 sm:grid-cols-3">
                                                                    <div className="space-y-2 sm:col-span-1">
                                                                        <div className="flex items-center justify-between h-6">
                                                                            <Label>Key</Label>
                                                                        </div>
                                                                        <Input
                                                                            value={item.key}
                                                                            onChange={(e) => updateCustomField(fieldName, itemIndex, 'key', e.target.value)}
                                                                            placeholder="Enter key"
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-2 sm:col-span-1">
                                                                        <div className="flex items-center justify-between h-6">
                                                                            <Label>Data Type</Label>
                                                                        </div>
                                                                        <Combobox
                                                                            options={[
                                                                                { value: 'string', label: 'String' },
                                                                                { value: 'number', label: 'Number' },
                                                                                { value: 'boolean', label: 'Boolean' }
                                                                            ]}
                                                                            value={item.dataType}
                                                                            onValueChange={(value) => updateCustomField(fieldName, itemIndex, 'dataType', value as CustomFieldDataType)}
                                                                            placeholder="Select data type..."
                                                                            searchPlaceholder="Search data types..."
                                                                            emptyMessage="No data type found."
                                                                        />
                                                                    </div>
                                                                    <div className="space-y-2 sm:col-span-1">
                                                                        <div className="flex items-center justify-between h-6">
                                                                            <Label>Value</Label>
                                                                            {field.items.length > 1 && (
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    onClick={() => removeKeyValuePair(fieldName, itemIndex)}
                                                                                    className="h-6 px-2 text-destructive hover:text-destructive"
                                                                                >
                                                                                    <Trash2 className="h-3 w-3" />
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                        {item.dataType === 'string' && (
                                                                            <Input
                                                                                value={typeof item.value === 'string' ? item.value : ''}
                                                                                onChange={(e) => updateCustomField(fieldName, itemIndex, 'value', e.target.value)}
                                                                                placeholder="Enter string value"
                                                                            />
                                                                        )}
                                                                        {item.dataType === 'number' && (
                                                                            <Input
                                                                                type="number"
                                                                                value={typeof item.value === 'number' ? item.value : ''}
                                                                                onChange={(e) => updateCustomField(fieldName, itemIndex, 'value', e.target.value === '' ? undefined : Number(e.target.value))}
                                                                                placeholder="Enter number value"
                                                                            />
                                                                        )}
                                                                        {item.dataType === 'boolean' && (
                                                                            <Combobox
                                                                                options={[
                                                                                    { value: 'true', label: 'True' },
                                                                                    { value: 'false', label: 'False' }
                                                                                ]}
                                                                                value={String(item.value ?? 'false')}
                                                                                onValueChange={(value) => updateCustomField(fieldName, itemIndex, 'value', value === 'true')}
                                                                                placeholder="Select boolean value..."
                                                                                searchPlaceholder="Search values..."
                                                                                emptyMessage="No value found."
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </motion.div>
                                                        ))}
                                                        <div className="flex justify-end mt-2">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => addKeyValuePair(fieldName)}
                                                                className="text-xs"
                                                            >
                                                                <Plus className="h-3 w-3 mr-1" />
                                                                Add Item
                                                            </Button>
                                                        </div>
                                                        </CardContent>
                                                    </Card>
                                                </motion.div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Help Documentation */}
                    <motion.div variants={itemVariants}>
                        <Card className="overflow-hidden">
                            <CardHeader className="bg-muted/30">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                        <Info className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <CardTitle>Instructions</CardTitle>
                                        <CardDescription>Learn how to use custom evaluation criteria</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Link evaluation criteria to your Assistant's Structured Data fields for precise analysis.
                                </p>

                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">Add custom fields</p>
                                            <p className="text-xs text-muted-foreground">Create fields matching your Assistant's structured data</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">Define key-value pairs</p>
                                            <p className="text-xs text-muted-foreground">Set criteria using the same keys and values from your data</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium">Match exactly</p>
                                            <p className="text-xs text-muted-foreground">Field names and values must match your structured data precisely</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-1 pt-3 border-t">
                                    <div className="rounded-lg bg-muted/30 p-3">
                                        <p className="text-xs font-medium mb-2">Example</p>
                                        <div className="font-mono text-xs space-y-1">
                                            <div className="text-primary font-medium">contactReasonEval</div>
                                            <div className="ml-3 text-muted-foreground">
                                                <div>• contactReason: <span className="text-foreground">"Product FAQ"</span></div>
                                                <div>• country: <span className="text-foreground">"Germany"</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </form>
        </motion.div>
    );
};

export default RubricForm; 